import { getDocument } from '@atlaskit/browser-apis';

/**
 * Optimized DOM injection manager for banner positioning.
 * Handles insertion and cleanup of session banners as first child of body
 */
export class BannerManager {
	private bannerContainer: HTMLElement | null = null;

	createBannerContainer(): HTMLElement {
		const doc = getDocument();
		// Return existing container if already present in DOM
		if (this.bannerContainer && doc?.contains(this.bannerContainer)) {
			return this.bannerContainer;
		}

		// Clean up stale references
		this.cleanup();

		// Create new container with minimal DOM operations
		const container = doc?.createElement('div');
		if (!container) {
			throw new Error('Failed to create banner container: document not available');
		}
		container.id = 'session-banner-container';
		container.setAttribute('data-banner-type', 'session-expiry');

		// Use cssText for batch style assignment (more performant than individual properties)
		container.style.cssText = 'position:relative;width:100%;z-index:9999;';

		// Insert as first child of body with fallback
		try {
			doc?.body.insertBefore(container, doc.body.firstElementChild);
		} catch (error) {
			// Fallback: append if insertBefore fails (shouldn't happen in practice)
			// Silent failure - DOM insertion errors should not interfere with application
			doc?.body.appendChild(container);
		}

		this.bannerContainer = container;
		return container;
	}

	cleanup(): void {
		const doc = getDocument();
		if (this.bannerContainer && doc?.contains(this.bannerContainer)) {
			this.bannerContainer.remove();
		}
		this.bannerContainer = null;
	}

	getContainer(): HTMLElement | null {
		return this.bannerContainer;
	}
}
